import React, { useRef, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, CheckCircle, AlertTriangle, Clock, ArrowRight, Store, FileText, FileSignature } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useNotificacoesFinanceiras } from '../../hooks/useNotificacoesFinanceiras'
import { formatCurrency, formatDate } from '../../utils/formatters'

interface NotificacoesDropdownProps {
  isOpen: boolean
  onClose: () => void
  anchorRef: React.RefObject<HTMLElement | null>
}

interface ParcelaItem {
  id: string
  lancamento_id: string
  numero_parcela: number
  valor: number
  data_vencimento: string
  situacao: string
  tipo: 'PAGAR' | 'RECEBER'
  descricao: string
  pessoa_nome: string | null
  dias_atraso: number
}

export default function NotificacoesDropdown({ isOpen, onClose, anchorRef }: NotificacoesDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { notificacoes, totalCount, loading } = useNotificacoesFinanceiras()
  const { caixas_abertos_dia_anterior, contratos_a_vencer } = notificacoes

  // Spinner só na primeira carga: marca quando loading passa de true para false
  const [jaCarregou, setJaCarregou] = useState(false)
  const loadingAnteriorRef = useRef(loading)
  useEffect(() => {
    if (loadingAnteriorRef.current && !loading) {
      setJaCarregou(true)
    }
    loadingAnteriorRef.current = loading
  }, [loading])

  // Fecha ao clicar fora
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose, anchorRef])

  // Posiciona abaixo do botão
  useEffect(() => {
    if (isOpen && anchorRef.current && dropdownRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect()
      const dropdown = dropdownRef.current

      dropdown.style.top = `${anchorRect.bottom + 8}px`
      dropdown.style.right = `${window.innerWidth - anchorRect.right}px`
      dropdown.style.left = 'auto'
    }
  }, [isOpen, anchorRef])

  if (!isOpen) return null

  const temVenceHoje = notificacoes.vence_hoje_pagar.length > 0 || notificacoes.vence_hoje_receber.length > 0
  const temVencidas = notificacoes.vencidas_pagar.length > 0 || notificacoes.vencidas_receber.length > 0
  const temBoletosNaoGerados = notificacoes.boletos_nao_gerados.length > 0
  const temCaixaAberto = caixas_abertos_dia_anterior.length > 0
  const temContratoAVencer = contratos_a_vencer.length > 0

  const handleItemClick = (item: ParcelaItem) => {
    onClose()
    if (item.tipo === 'PAGAR') {
      navigate(`/financeiro/contas-pagar?lancamento_id=${item.lancamento_id}`)
    } else {
      navigate(`/financeiro/contas-receber?lancamento_id=${item.lancamento_id}`)
    }
  }

  const renderItem = (item: ParcelaItem) => (
    <button
      key={item.id}
      onClick={() => handleItemClick(item)}
      className="w-full text-left p-3 rounded-xl hover:bg-surface/50 transition-all duration-200 group cursor-pointer"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                item.tipo === 'PAGAR'
                  ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400'
                  : 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400'
              }`}
            >
              {item.tipo}
            </span>
            {item.pessoa_nome && (
              <span className="text-xs text-text-secondary truncate">{item.pessoa_nome}</span>
            )}
          </div>
          <p className="text-xs text-text-muted truncate">{item.descricao || `Parcela ${item.numero_parcela}`}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-semibold text-text-primary">{formatCurrency(item.valor)}</p>
          <div className="flex items-center gap-1 justify-end">
            <span className="text-[10px] text-text-muted">{formatDate(item.data_vencimento)}</span>
            {item.dias_atraso > 0 && (
              <span className="text-[10px] font-medium text-red-400">
                {item.dias_atraso}d
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-[9999] w-[25rem] bg-surface-light border border-border-primary/50 rounded-2xl shadow-2xl"
      style={{ animation: 'scaleIn 0.2s ease-out' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-primary/30">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-text-primary">Pendencias Financeiras</h3>
          {totalCount > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400">
              {totalCount}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface/50 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-hide">
        {loading && !jaCarregou ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : totalCount === 0 ? (
          /* Estado vazio */
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <CheckCircle className="w-10 h-10 text-emerald-400 mb-3" />
            <p className="text-sm font-medium text-text-primary">Nenhuma pendencia financeira</p>
            <p className="text-xs text-text-muted mt-1">Todas as contas estao em dia</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {/* Caixas sem fechar */}
            {temCaixaAberto && (
              <div>
                <div className="flex items-center gap-2 px-3 py-2">
                  <Store className="w-3.5 h-3.5 text-orange-400" />
                  <span className="text-[11px] font-semibold text-orange-400 uppercase tracking-wider">
                    Caixa sem fechar
                  </span>
                </div>
                {caixas_abertos_dia_anterior.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { onClose(); navigate('/financeiro/caixa-diario') }}
                    className="w-full text-left p-3 rounded-xl hover:bg-surface/50 transition-all duration-200 group cursor-pointer"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-text-primary truncate">{c.terminal_nome}</p>
                        <p className="text-[11px] text-text-muted">Aberto em {formatDate(c.data_caixa)}</p>
                      </div>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                        ABERTO
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Contratos a vencer */}
            {temContratoAVencer && (
              <div>
                <div className="flex items-center gap-2 px-3 py-2">
                  <FileSignature className="w-3.5 h-3.5 text-purple-400" />
                  <span className="text-[11px] font-semibold text-purple-400 uppercase tracking-wider">
                    Contratos a vencer
                  </span>
                </div>
                {contratos_a_vencer.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { onClose(); navigate('/contratos') }}
                    className="w-full text-left p-3 rounded-xl hover:bg-surface/50 transition-all duration-200 group cursor-pointer"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-text-primary truncate">
                          nº {c.numero} — {c.titulo}
                        </p>
                        {c.pessoa_nome && (
                          <p className="text-[11px] text-text-secondary truncate">{c.pessoa_nome}</p>
                        )}
                        <p className="text-[11px] text-text-muted">Vence em {formatDate(c.data_fim)}</p>
                      </div>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 shrink-0">
                        {c.dias_restantes === 0 ? 'HOJE' : `${c.dias_restantes}d`}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Boletos não gerados */}
            {temBoletosNaoGerados && (
              <div>
                <div className="flex items-center gap-2 px-3 py-2">
                  <FileText className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-[11px] font-semibold text-blue-400 uppercase tracking-wider">
                    Boletos não gerados
                  </span>
                </div>
                {notificacoes.boletos_nao_gerados.map(renderItem)}
              </div>
            )}

            {/* Vence Hoje */}
            {temVenceHoje && (
              <div>
                <div className="flex items-center gap-2 px-3 py-2">
                  <Clock className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-[11px] font-semibold text-amber-400 uppercase tracking-wider">
                    Vence Hoje
                  </span>
                </div>
                {notificacoes.vence_hoje_pagar.map(renderItem)}
                {notificacoes.vence_hoje_receber.map(renderItem)}
              </div>
            )}

            {/* Vencidas */}
            {temVencidas && (
              <div>
                <div className="flex items-center gap-2 px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  <span className="text-[11px] font-semibold text-red-400 uppercase tracking-wider">
                    Vencidas
                  </span>
                </div>

                {notificacoes.vencidas_pagar.length > 0 && (
                  <div>
                    <p className="text-[10px] text-text-muted px-3 py-1 uppercase tracking-wider">A Pagar</p>
                    {notificacoes.vencidas_pagar.map(renderItem)}
                  </div>
                )}

                {notificacoes.vencidas_receber.length > 0 && (
                  <div>
                    <p className="text-[10px] text-text-muted px-3 py-1 uppercase tracking-wider">A Receber</p>
                    {notificacoes.vencidas_receber.map(renderItem)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {totalCount > 0 && (
        <div className="border-t border-border-primary/30 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => { onClose(); navigate('/financeiro/contas-pagar') }}
            className="flex items-center gap-1 text-xs text-text-muted hover:text-primary-accent transition-colors"
          >
            Ver contas a pagar
            <ArrowRight className="w-3 h-3" />
          </button>
          {temCaixaAberto && (
            <button
              onClick={() => { onClose(); navigate('/financeiro/caixa-diario') }}
              className="flex items-center gap-1 text-xs text-text-muted hover:text-orange-400 transition-colors"
            >
              Ver caixa diário
              <ArrowRight className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={() => { onClose(); navigate('/financeiro/contas-receber') }}
            className="flex items-center gap-1 text-xs text-text-muted hover:text-primary-accent transition-colors"
          >
            Ver contas a receber
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>,
    document.body
  )
}
